import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth.jsx";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import LogoUpload from "@/components/LogoUpload";
import ColorPicker from "@/components/ColorPicker";
import BrandMark from "@/components/BrandMark";
import MiniOfficePreview from "@/components/MiniOfficePreview";
import { toast } from "sonner";
import * as log from "@/lib/logger";

export default function Onboarding() {
  const { currentOrg, refreshMe } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [orgName, setOrgName] = useState(currentOrg?.name || "");
  const [primary, setPrimary] = useState(currentOrg?.primary_color || "#1A1A1A");
  const [accent, setAccent] = useState(currentOrg?.accent_color || "#D95D39");
  const [logoUrl, setLogoUrl] = useState(currentOrg?.logo_url || "");
  const [inviteEmails, setInviteEmails] = useState("");
  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState("compact_office");
  const [previewTpl, setPreviewTpl] = useState(null);
  const [channels, setChannels] = useState("announcements,general,team-updates");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/office/templates").then((r) => setTemplates(r.data)).catch(() => setTemplates([]));
  }, []);

  const finish = async () => {
    setBusy(true);
    try {
      await api.patch("/orgs/current", {
        name: orgName, logo_url: logoUrl, primary_color: primary, accent_color: accent,
      });
      const chList = channels.split(",").map((c) => c.trim().toLowerCase()).filter(Boolean);
      // Every fresh org already has announcements/general/team-updates seeded server-side,
      // so only create channels the user actually added on top of that.
      let existing = [];
      try {
        const r = await api.get("/channels");
        existing = (r.data || []).map((c) => (c.name || "").toLowerCase());
      } catch (err) { log.debug("[onboarding] list channels failed", err); }
      const existingSet = new Set(existing);
      for (const c of chList) {
        if (existingSet.has(c)) continue;
        try { await api.post("/channels", { name: c }); existingSet.add(c); }
        catch (err) { log.debug("[onboarding] channel create failed", c, err); }
      }
      const emails = inviteEmails.split(",").map((s) => s.trim()).filter(Boolean);
      for (const em of emails) {
        try { await api.post("/auth/invite", { email: em }); }
        catch (err) { log.debug("[onboarding] invite failed", em, err); }
      }
      // Apply the chosen office template and publish it as the initial layout.
      try {
        await api.post("/office/draft/apply-template", { template_id: tpl });
        await api.post("/office/publish");
      } catch (err) { log.debug("[onboarding] template apply failed", err); }
      await api.post("/orgs/current/complete-onboarding");
      await refreshMe();
      toast.success("Workspace ready");
      nav("/app/office");
    } catch { toast.error("Onboarding failed"); }
    finally { setBusy(false); }
  };

  if (!currentOrg) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-sm text-zinc-500">Preparing workspace…</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--bg-app)] flex items-center justify-center p-6" data-testid="onboarding-page">
      <div className="w-full max-w-3xl bg-white border rounded-2xl p-8 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <BrandMark theme="light"/>
          <div className="text-xs tracking-[0.2em] text-zinc-500">STEP {step} OF 5</div>
        </div>
        <div className="h-1 bg-zinc-100 rounded-full overflow-hidden mb-8">
          <div className="h-full bg-[var(--brand-accent)]" style={{ width: `${(step/5)*100}%` }} />
        </div>

        {step === 1 && (
          <div>
            <h2 className="text-3xl font-semibold mb-2">Name your workspace</h2>
            <p className="text-zinc-500 mb-6">You can rebrand at any time in Workspace Settings.</p>
            <Label>Organization name</Label>
            <Input value={orgName} onChange={(e) => setOrgName(e.target.value)} data-testid="onb-org-name" className="h-11"/>
          </div>
        )}

        {step === 2 && (
          <div>
            <h2 className="text-3xl font-semibold mb-2">Add a company logo</h2>
            <p className="text-zinc-500 mb-6">Optional — appears in the sidebar and workspace headers.</p>
            <LogoUpload value={logoUrl} onChange={setLogoUrl} onSkip={() => setStep(3)}/>
          </div>
        )}

        {step === 3 && (
          <div>
            <h2 className="text-3xl font-semibold mb-2">Choose your brand colors</h2>
            <p className="text-zinc-500 mb-6">Select a preset or enter your own custom colors. You can change them later in Workspace Settings.</p>
            <ColorPicker primary={primary} accent={accent} onPrimary={setPrimary} onAccent={setAccent}/>
          </div>
        )}

        {step === 4 && (
          <div>
            <h2 className="text-3xl font-semibold mb-2">Invite your team</h2>
            <p className="text-zinc-500 mb-6">Comma-separated emails. You can invite anyone later from Members.</p>
            <Input value={inviteEmails} onChange={(e) => setInviteEmails(e.target.value)} placeholder="jane@company.com, sam@company.com" data-testid="onb-invite" className="h-11"/>
            <div className="mt-6">
              <Label className="mb-2 block">Starter channels</Label>
              <Input value={channels} onChange={(e) => setChannels(e.target.value)} data-testid="onb-channels" className="h-11"/>
              <div className="text-xs text-zinc-500 mt-1">Generic defaults — customize any of these later. e.g. announcements, general, team-updates</div>
            </div>
          </div>
        )}

        {step === 5 && (
          <div>
            <h2 className="text-3xl font-semibold mb-2">Choose an office template</h2>
            <p className="text-zinc-500 mb-6">Every template is fully editable in Workspace Settings after setup.</p>
            <div className="grid md:grid-cols-2 gap-3" data-testid="onb-templates">
              {templates.map((t) => (
                <div key={t.id}
                  data-testid={`onb-tpl-${t.id}`}
                  className={`relative text-left p-3 rounded-xl border-2 transition ${tpl===t.id ? "border-[var(--brand-accent)] bg-orange-50" : "border-zinc-200 hover:border-zinc-300"}`}>
                  <button type="button" onClick={() => setTpl(t.id)}
                    className="w-full text-left" data-testid={`onb-tpl-select-${t.id}`}>
                    <MiniOfficePreview layout={{ grid: t.grid, rooms: t.rooms }} width={300} height={130} className="mb-3" data-testid={`onb-tpl-preview-${t.id}`}/>
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{t.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{t.room_count} rooms · {t.seat_count} seats</div>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1">{t.description}</div>
                  </button>
                  <button type="button" onClick={() => setPreviewTpl(t)}
                    className="absolute top-2 right-2 text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-white/90 border hover:bg-white"
                    data-testid={`onb-tpl-preview-btn-${t.id}`}>
                    Preview
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8 flex justify-between items-center">
          <Button variant="ghost" disabled={step === 1} onClick={() => setStep(step - 1)} data-testid="onb-back">Back</Button>
          <div className="text-xs text-zinc-500">You can revisit every step in <b>Workspace Settings</b>.</div>
          {step < 5 ? (
            <Button className="bg-[var(--brand-primary)] text-white" onClick={() => setStep(step + 1)} data-testid="onb-next">Continue</Button>
          ) : (
            <Button disabled={busy} className="bg-[var(--brand-accent)] text-white" onClick={finish} data-testid="onb-finish">{busy ? "Setting up…" : "Enter workspace"}</Button>
          )}
        </div>
      </div>

      <Dialog open={!!previewTpl} onOpenChange={(o) => !o && setPreviewTpl(null)}>
        <DialogContent className="max-w-3xl" aria-describedby="template-preview-desc">
          {previewTpl && (
            <>
              <DialogHeader>
                <DialogTitle>{previewTpl.name}</DialogTitle>
                <DialogDescription id="template-preview-desc">
                  {previewTpl.description} · {previewTpl.room_count} rooms · {previewTpl.seat_count} seats
                </DialogDescription>
              </DialogHeader>
              <div className="flex justify-center">
                <MiniOfficePreview layout={{ grid: previewTpl.grid, rooms: previewTpl.rooms }} width={720} height={460} showLabels data-testid="onb-tpl-modal-preview"/>
              </div>
              <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-1 text-xs text-zinc-600">
                {(previewTpl.rooms || []).map((r) => (
                  <div key={r.id} className="flex items-center gap-2">
                    <span className="w-3 h-3 rounded" style={{ background: r.color }}/>
                    <span>{r.name}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setPreviewTpl(null)} data-testid="onb-tpl-modal-close">Close</Button>
                <Button className="bg-[var(--brand-primary)] text-white" onClick={() => { setTpl(previewTpl.id); setPreviewTpl(null); }} data-testid="onb-tpl-modal-select">Select this template</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
